import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { salesApi } from '../../services/salesApi.js';

const PRODUCT_LOOKUP_LIMIT = 200;
const DEFAULT_MIN_QUANTITY = '1';

function emptyItem() {
  return { productId: '', minQuantity: DEFAULT_MIN_QUANTITY, unitPrice: '0' };
}

function initialForm(priceList) {
  if (!priceList) {
    return { name: '', isActive: true, notes: '', items: [emptyItem()] };
  }
  return {
    name: priceList.name || '',
    isActive: Boolean(priceList.isActive),
    notes: priceList.notes || '',
    items: (priceList.items || []).length > 0
      ? priceList.items.map((it) => ({
          productId: it.productId || '',
          // NUMERIC strings like '90.0000' → display without trailing zeros.
          minQuantity: String(Number(it.minQuantity ?? 1)),
          unitPrice: String(Number(it.unitPrice ?? 0)),
        }))
      : [emptyItem()],
  };
}

/**
 * Create/edit modal for a price list. For edit mode the parent fetches the
 * full list (incl. items) first and passes it as `priceList`. Mount with a
 * `key` per price list so the form state resets between targets.
 * Multiple rows for the same product with different minimum quantities form
 * the quantity tiers; duplicate (product, minQuantity) pairs are rejected
 * client-side before submit.
 */
export function PriceListModal({ open, onClose, priceList = null }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(() => initialForm(priceList));
  const [error, setError] = useState(null);

  const products = useQuery({
    queryKey: ['inventory', 'products'],
    queryFn: () => inventoryApi.listProducts({ limit: PRODUCT_LOOKUP_LIMIT }).then((r) => r.items || []),
    enabled: open,
  });

  const save = useMutation({
    mutationFn: (body) =>
      priceList ? salesApi.updatePriceList(priceList.id, body) : salesApi.createPriceList(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales', 'price-lists'] });
      setError(null);
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function patchField(key, value) {
    setForm((p) => ({ ...p, [key]: value }));
  }

  function patchLine(idx, key, value) {
    setForm((p) => ({
      ...p,
      items: p.items.map((item, i) => (i === idx ? { ...item, [key]: value } : item)),
    }));
  }

  function addLine() {
    setForm((p) => ({ ...p, items: [...p.items, emptyItem()] }));
  }

  function removeLine(idx) {
    setForm((p) => ({
      ...p,
      items: p.items.length > 1 ? p.items.filter((_, i) => i !== idx) : p.items,
    }));
  }

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) {
      setError(t('priceList.form.invalid'));
      return;
    }
    // Rows without a product are treated as blanks (an empty list is allowed).
    const filled = form.items.filter((it) => it.productId);
    const seen = new Set();
    for (const it of filled) {
      const pair = `${it.productId}|${Number(it.minQuantity)}`;
      if (seen.has(pair)) {
        setError(t('priceList.duplicateTier'));
        return;
      }
      seen.add(pair);
    }
    save.mutate({
      name: form.name.trim(),
      isActive: form.isActive,
      notes: form.notes.trim() || undefined,
      items: filled.map((it) => ({
        productId: it.productId,
        minQuantity: Number(it.minQuantity || 1),
        unitPrice: Number(it.unitPrice || 0),
      })),
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={priceList ? t('priceList.modal.editTitle') : t('priceList.action.new')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={save.isPending}>
            {save.isPending ? `${t('common.save')}…` : priceList ? t('common.save') : t('common.create')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('col.name')}</span>
            <input
              className="input"
              value={form.name}
              onChange={(e) => patchField('name', e.target.value)}
              required
            />
          </label>
          <label className="field" style={{ flex: '0 0 auto', flexDirection: 'row', alignItems: 'center', gap: 'var(--space-2)', alignSelf: 'flex-end' }}>
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => patchField('isActive', e.target.checked)}
            />
            <span className="field-label" style={{ margin: 0 }}>{t('priceList.form.active')}</span>
          </label>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span className="field-label">{t('priceList.items')}</span>
          <Button size="sm" variant="ghost" onClick={addLine}>+ {t('finance.invoices.modal.addLine')}</Button>
        </div>
        <p className="muted text-sm" style={{ margin: 0 }}>{t('priceList.tierHint')}</p>
        {form.items.map((item, idx) => (
          <div
            key={idx}
            style={{
              display: 'flex', flexDirection: 'column', gap: 'var(--space-2)',
              padding: 'var(--space-3)', border: '1px solid var(--color-hairline)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <label className="field">
              <span className="field-label">{t('priceList.form.product')}</span>
              <select
                className="select"
                value={item.productId}
                onChange={(e) => patchLine(idx, 'productId', e.target.value)}
              >
                <option value="">{t('inv.po.modal.pickProduct')}</option>
                {(products.data || []).map((p) => (
                  <option key={p.id} value={p.id}>{p.sku} - {p.name}</option>
                ))}
              </select>
            </label>
            <div className="field-row">
              <label className="field">
                <span className="field-label">{t('priceList.form.minQuantity')}</span>
                <input
                  className="input"
                  type="number"
                  min="0.0001"
                  step="any"
                  value={item.minQuantity}
                  onChange={(e) => patchLine(idx, 'minQuantity', e.target.value)}
                  required
                />
              </label>
              <label className="field">
                <span className="field-label">{t('priceList.form.unitPrice')}</span>
                <input
                  className="input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={item.unitPrice}
                  onChange={(e) => patchLine(idx, 'unitPrice', e.target.value)}
                  required
                />
              </label>
            </div>
            {form.items.length > 1 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button size="sm" variant="danger" onClick={() => removeLine(idx)}>
                  {t('finance.invoices.modal.removeLine')}
                </Button>
              </div>
            )}
          </div>
        ))}

        <label className="field">
          <span className="field-label">{t('finance.invoices.modal.notes')}</span>
          <textarea
            className="textarea"
            rows={2}
            value={form.notes}
            onChange={(e) => patchField('notes', e.target.value)}
          />
        </label>

        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
